import { parlorRegion } from "@/constants/parlorRegion";
import {
  BTypeCardView,
  // call,
  // ClickEventType,
  // ColorIcon,
  // colors,
  // ColorSystemProps,
  // CTypeCTA,
  // CTypeParlorList,
  // customEvent,
  FullWidthRadiusButton,
  FUNERAL_SIDO,
  FUNERAL_SIGUNGU,
  buildCrematoriumPageSchema,
  CrematoriumRegionFAQ,
  GOI_WEB_URL,
  RichSearch,
  // HLine,
  SidoName,
} from "goi_common";

import {
  Box,
  // Button, HStack,
  Stack,
  VStack,
} from "@chakra-ui/react";

import crematoriums from "goi_common/src/constants/hwajang_detail.json";
import { GetStaticPaths, GetStaticProps } from "next";

type CrematoriumDetail = (typeof crematoriums)[number];

import Head from "next/head";

const { targetSidos, regionColor } = parlorRegion;

export const getStaticPaths: GetStaticPaths = async () => {
  const sidoList = Array.from(FUNERAL_SIDO.entries()).filter(([code]) =>
    targetSidos.includes(code.toString())
  );

  const paths = sidoList
    .map((sido) => {
      const sigungus = FUNERAL_SIGUNGU.get(sido[0]);

      if (sigungus) {
        return Array.from(sigungus.values()).map((sigungu) => {
          return {
            params: {
              sido: sido[1],
              sigungu,
            },
          };
        });
      }

      return [];
    })
    .filter((i) => i != null);

  return {
    paths: paths.flat(),
    fallback: false,
  };
};

function getCrematoriumText(crematorium: CrematoriumDetail): string {
  if (!crematorium) {
    return "";
  }

  const companyname = crematorium?.companyname ?? "";
  const inside_price = crematorium?.inside_price ?? "";
  const outside_price = crematorium?.outside_price ?? "";

  const reserveText = companyname ? `${companyname} 예약` : "";
  const costText =
    inside_price && outside_price
      ? `, 비용 ${inside_price} ~ ${outside_price}`
      : "";

  const text = `${reserveText}${costText}`;

  return text;
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const { sido, sigungu } = params as { sido: string; sigungu: string };

  const sidoCode = Array.from(FUNERAL_SIDO.entries()).find(
    ([, value]) => value === SidoName[sido as keyof typeof SidoName]
  )?.[0];

  const sigunguCode = Array.from(FUNERAL_SIGUNGU.values())
    .flatMap((m) => Array.from(m.entries()))
    .find(([, name]) => name === sigungu)?.[0];

  /** 시군구까지 일치하는 것을 앞으로 배치, 시도일치를 뒤로 배치 */
  const regionCrematoriums = crematoriums
    .filter((c) => targetSidos.includes(c.sido.toString()))
    .sort((a, b) => {
      // 시도 일치 여부 비교 (1순위)
      const sidoCompare =
        Number(b.sido === Number(sidoCode)) -
        Number(a.sido === Number(sidoCode));

      if (sidoCompare !== 0) return sidoCompare;

      // 시군구 일치 여부 비교 (2순위)
      return (
        Number(b.sigungu === Number(sigunguCode)) -
        Number(a.sigungu === Number(sigunguCode))
      );
    });

  const district =
    sigungu.length >= 3 && (sigungu.endsWith("시") || sigungu.endsWith("구"))
      ? sigungu.slice(0, -1)
      : sigungu;

  const regionText = `${sido} ${district}`;

  const firstCrematorium = regionCrematoriums?.[0];
  const secondCrematorium = regionCrematoriums?.[1];

  const title = `${regionText} 화장장, 화장터 비용, 예약 총 정보 안내 및 요약 :: 부산울산경남장례정보포털`;

  const description = `${regionText} 화장장(화장터) 추천, 시설, 예약, 비용 종합 안내 ${getCrematoriumText(
    firstCrematorium
  )} ${getCrematoriumText(
    secondCrematorium
  )} 등 ${regionText} 화장장, 화장터 위치, 이용료, 예약방법 및 장례 서비스 정보 제공`;

  const keywords = `${regionText} 화장장, ${sido} 화장장, ${sigungu} 화장장, ${district} 화장장, 
  ${regionText} 화장터, ${sido} 화장터, ${sigungu} 화장터, ${district} 화장터, 
  ${regionText} 화장장 추천, ${sido} 화장장 추천, ${sigungu} 화장장 추천, ${district} 화장장 추천, 
  ${regionText} 화장장 정보, ${sido} 화장장 정보, ${sigungu} 화장장 정보, ${district} 화장장 정보, 
  ${regionText} 화장장 비교, ${sido} 화장장 비교, ${sigungu} 화장장 비교, ${district} 화장장 비교, 
  ${regionText} 화장장 예약, ${sido} 화장장 예약, ${sigungu} 화장장 예약, ${district} 화장장 예약,
  ${regionText} 화장장 비용, ${sido} 화장장 비용, ${sigungu} 화장장 비용, ${district} 화장장 비용,
  ${regionText} 화장터 추천, ${sido} 화장터 추천, ${sigungu} 화장터 추천, ${district} 화장터 추천, 
  ${regionText} 화장터 정보, ${sido} 화장터 정보, ${sigungu} 화장터 정보, ${district} 화장터 정보, 
  ${regionText} 화장터 비교, ${sido} 화장터 비교, ${sigungu} 화장터 비교, ${district} 화장터 비교, 
  ${regionText} 화장터 예약, ${sido} 화장터 예약, ${sigungu} 화장터 예약, ${district} 화장터 예약,
  ${regionText} 화장터 비용, ${sido} 화장터 비용, ${sigungu} 화장터 비용, ${district} 화장터 비용,
  ${sido} 화장예약, ${sido} 화장 비용, 
  ${sigungu} 화장예약, ${sigungu} 화장 비용,
  ${regionText} 화장예약, ${regionText} 화장 비용,
  ${district} 화장예약, ${district} 화장 비용,
  ${regionCrematoriums
    ?.map((c) => {
      const name =
        c.companyname.includes("화장장") || c.companyname.includes("화장터")
          ? c.companyname
          : `${c.companyname} 화장장`;
      return `${name}, ${name} 화장비용, ${name} 화장예약, ${name} 서류, ${name} 시간, ${name} 시설, ${name} 유택동산, ${name} 근처 화장장, ${name} 근처 화장터`;
    })
    .join(", ")}, 화장장, 화장터, 화장예약, 화장 비용, 화장지원금`;

  return {
    props: {
      sidoName: sido,
      sigunguName: sigungu,
      regionCrematoriums,
      district,
      pageDescription: description,
      pageKeywords: keywords,
      pageOgTitle: title,
      pageOgDescription: description,
      pageOgImage: regionCrematoriums[0]?.images[0]?.url,
    },
  };
};

function CrematoriumRegionSigunguPage({
  sidoName,
  sigunguName,
  regionCrematoriums,
  district,
  pageOgTitle,
}: {
  sidoName: string;
  sigunguName: string;
  regionCrematoriums: CrematoriumDetail[];
  district: string;
  pageOgTitle: string;
}) {
  const defaultCrematorium = regionCrematoriums[0];
  const totalCrematoriums = regionCrematoriums.length;

  const CrematoriumRegionHeroSection = () => {
    return (
      <VStack
        width="100%"
        style={{
          background: regionColor.c100,
        }}
      >
        <img
          src={
            defaultCrematorium?.images[0]?.url
              ? defaultCrematorium?.images[0]?.url
              : ""
          }
          alt={`${defaultCrematorium?.companyname} 외부 시설 사진`}
          width="100%"
          height="100%"
        />
      </VStack>
    );
  };

  const regionText = `${sidoName} ${district}`;

  const crematoriumPageSchema = buildCrematoriumPageSchema(regionCrematoriums, {
    regionText,
    goiWebUrl: GOI_WEB_URL,
  });

  return (
    <>
      <Head>
        <title>
          {pageOgTitle}
          {/* ({sidoName}) {sigunguName} 화장터 및 화장터 시설 비용 및 예약 ::
          충청대전세종장례소개포털 */}
        </title>
      </Head>
      <RichSearch data={crematoriumPageSchema} />

      <main>
        <section
          style={{
            background: "white",
            margin: "0px 12px",
            boxShadow: "0px 0px 12px 0px rgba(0, 0, 0, 0.12)",
          }}
        >
          <Box margin="0px 0px 20px 0px">
            <CrematoriumRegionHeroSection />
          </Box>
          <VStack padding="20px 20px" alignItems="center" textAlign="center">
            <h1 className="head_24_b">
              {regionText} 화장장(화장터)
              <br />
              비용 및 예약 정보
            </h1>
            <p
              className="body_14_m gray700"
              style={{
                marginBottom: "5px",
              }}
            >
              {regionText} 및 인접 지역에 거주중이시라면
              <br /> 아래에서 화장장, 화장터 시설 비용 및 예약, 서류, 이용료
              혜택 정보를 확인해보세요.
            </p>
          </VStack>
        </section>

        <section
          style={{
            background: "white",
            margin: "20px 12px",
            padding: "40px 0px 20px",
            boxShadow: "0px 0px 12px 0px rgba(0, 0, 0, 0.12)",
          }}
        >
          <VStack padding="10px 20px">
            <VStack textAlign="center" gap="10px">
              <p className="subtitle_16_sb gray900">
                {regionText} 지역 화장장(화장터)의 비용, 예약, 서류, 혜택 정보를
                안내해 드립니다.
              </p>
              <p className="body_14_m gray800">
                화장터(화장장)의 경우 주소지 기준의 관내/관외 대상
                <br /> 적용 여부에 따라 비용이 다르기 때문에
                <br /> 꼭 확인해보시는게 좋습니다.
              </p>
            </VStack>

            <Stack gap="10px" marginTop="30px">
              <FullWidthRadiusButton
                backgroundColor={regionColor.c400}
                text="주소지 기준 화장장 문의하기"
                href={"tel:1666-9784"}
                event={{
                  domain: "gyeongsang",
                  path: "crematorium",
                  trigger_and_target: "goi_call",
                }}
              />
            </Stack>
          </VStack>
        </section>

        <section style={{ padding: "20px 16px", marginTop: "20px" }}>
          <VStack
            width="100%"
            gap="5px"
            alignItems="flex-start"
            padding="0px 0px 10px 0px"
          >
            <h2 className="subtitle_16_b gray900">
              {regionText} 인접 지역 화장장
            </h2>
            <p className="body_14_m gray700">
              화장장 예약이 가능한 {totalCrematoriums}개의 화장장이 {regionText}
              지역 및 인접 지역에는 존재합니다.
            </p>
          </VStack>

          <VStack as="ul" width="100%" gap="16px">
            {regionCrematoriums.map((crematorium) => (
              <li key={crematorium?.id} style={{ width: "100%" }}>
                <BTypeCardView
                  descriptions={[crematorium?.address]}
                  arrowText="자세히 알아보기"
                  href={`${GOI_WEB_URL}/crematorium-recommendation/${crematorium?.id}/?utm_source=dogsound`}
                  imgSrc={
                    crematorium.images[0] ? crematorium.images[0].url : ""
                  }
                  name={crematorium.companyname}
                  mainColor={regionColor}
                  hasOneDayBasis={false}
                  event={{
                    domain: "gyeongsang",
                    path: "crematorium",
                    trigger_and_target: "item_click",
                  }}
                />
              </li>
            ))}
          </VStack>
        </section>

        <CrematoriumRegionFAQ />
      </main>
    </>
  );
}

export default CrematoriumRegionSigunguPage;
